import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLenis } from '../UI/SmoothScroll';
import FlipButton from '../UI/FlipButton';
import DarkModeToggle from '../UI/DarkModeToggle';
import Logo from '../UI/Logo';
import { LINKS } from '../../constants';

const NAV_LINKS = [
  { label: 'Work', target: '#projects' },
  { label: 'Approach', target: '#approach' },
  { label: 'Skills', target: '#skills' },
  { label: 'Experience', target: '#experience' },
];

interface NavbarProps {
  isDark: boolean;
  onToggleDark: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ isDark, onToggleDark }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollTo, setLocked } = useLenis();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setLocked(menuOpen);
    return () => setLocked(false);
  }, [menuOpen, setLocked]);

  const goTo = (target: string) => {
    setMenuOpen(false);
    requestAnimationFrame(() => scrollTo(target, -72));
  };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-paper/85 backdrop-blur-xl border-b border-line' : 'bg-transparent'
        }`}
      >
        <div className='max-w-7xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between'>
          <button
            onClick={() => goTo('#intro')}
            className='text-lg text-ink'
            aria-label='Akash Roy — back to top'
          >
            <Logo />
          </button>

          {/* Desktop links */}
          <nav className='hidden md:flex items-center gap-7'>
            {NAV_LINKS.map(link => (
              <button
                key={link.label}
                onClick={() => goTo(link.target)}
                className='group text-sm font-medium text-gray1 hover:text-ink transition-colors duration-300'
              >
                <span className='flip-label'>
                  <span>{link.label}</span>
                  <span aria-hidden='true'>{link.label}</span>
                </span>
              </button>
            ))}
          </nav>

          <div className='flex items-center gap-3'>
            <DarkModeToggle isDark={isDark} onToggle={onToggleDark} />
            <div className='hidden sm:block'>
              <FlipButton
                label='Get in touch'
                href={LINKS.linkedin}
                variant='dark'
                size='sm'
              />
            </div>
            <button
              onClick={() => setMenuOpen(true)}
              className='md:hidden p-2.5 -mr-2 text-ink'
              aria-label='Open menu'
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile fullscreen menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className='fixed inset-0 z-[70] bg-paper flex flex-col'
          >
            <div className='flex items-center justify-between px-5 h-16 border-b border-line'>
              <Logo className='text-lg text-ink' />
              <button
                onClick={() => setMenuOpen(false)}
                className='p-2.5 -mr-2 text-ink'
                aria-label='Close menu'
              >
                <X size={22} />
              </button>
            </div>

            <nav className='flex-1 flex flex-col justify-center px-7 gap-1'>
              {NAV_LINKS.map((link, i) => (
                <motion.button
                  key={link.label}
                  initial={{ opacity: 0, y: 26 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ delay: 0.07 + i * 0.06, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  onClick={() => goTo(link.target)}
                  className='group flex items-baseline gap-4 py-3 text-left'
                >
                  <span className='label-mono text-accent'>{String(i + 1).padStart(2, '0')}</span>
                  <span className='font-medium tracking-tight text-4xl text-ink'>{link.label}</span>
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{
                  delay: 0.07 + NAV_LINKS.length * 0.06,
                  duration: 0.4,
                  ease: [0.16, 1, 0.3, 1],
                }}
                onClick={() => goTo('#contact')}
                className='group flex items-baseline gap-4 py-3 text-left'
              >
                <span className='label-mono text-accent'>
                  {String(NAV_LINKS.length + 1).padStart(2, '0')}
                </span>
                <span className='font-medium tracking-tight text-4xl text-accent'>Contact</span>
              </motion.button>
            </nav>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.35 }}
              className='px-7 pb-9 label-mono text-gray1'
            >
              {LINKS.email}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
